import { useEffect, useMemo, useState } from "react";
import { MapPin, Search, Loader2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const norm = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

let cache = null;

export const LockerPicker = ({ value, onChange, invalid }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(cache || []);
  const [state, setState] = useState(cache ? "ready" : "idle");
  const [q, setQ] = useState("");

  const load = () => {
    setState("loading");
    api
      .get("/omniva/lockers")
      .then((r) => {
        cache = r.data.lockers;
        setItems(cache);
        setState("ready");
      })
      .catch(() => setState("error"));
  };

  useEffect(() => {
    if (open && state === "idle") load();
    if (!open) setQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    if (!words.length) return items;
    const matches = items.filter((i) => {
      const hay = norm(`${i.city} ${i.name} ${i.address} ${i.id}`);
      return words.every((w) => hay.includes(w));
    });
    // lockers in the searched city first (e.g. "riga" -> Rīga before "Rīgas iela" elsewhere)
    const score = (i) => (words.some((w) => norm(i.city).startsWith(w)) ? 1 : 0);
    return matches.map((i, idx) => [i, score(i), idx]).sort((a, b) => b[1] - a[1] || a[2] - b[2]).map((x) => x[0]);
  }, [items, q]);

  return (
    <>
      {value ? (
        <div className="flex items-start gap-3 rounded-xl border-[1.5px] border-black bg-white p-3" data-testid="checkout-locker-selected">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--rpm-lime)]">
            <MapPin className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-semibold leading-tight">{value.name}</div>
            <div className="mt-0.5 text-xs text-zinc-600">
              {value.address}, {value.city}
            </div>
          </div>
          <button type="button" onClick={() => setOpen(true)} className="rpm-btn rpm-btn-outline !min-h-[36px] !rounded-lg !px-3 text-xs" data-testid="checkout-locker-change-button">
            {t("checkout.lockerChange")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-invalid={invalid}
          className={`rpm-input flex w-full items-center gap-2 px-4 text-left text-zinc-500 hover:border-zinc-400 ${invalid ? "!border-red-600" : ""}`}
          data-testid="checkout-locker-input"
        >
          <MapPin className="h-4 w-4 shrink-0 text-black" />
          {t("checkout.lockerChoose")}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[88vh] w-[calc(100vw-24px)] max-w-lg flex-col gap-0 overflow-hidden rounded-2xl bg-white p-0" data-testid="locker-dialog">
          <DialogHeader className="border-b border-zinc-200 p-5 pb-4 text-left">
            <DialogTitle className="font-display text-lg">{t("checkout.lockerTitle")}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {state === "ready" ? t("checkout.lockerCount", { n: items.length }) : "\u00a0"}
            </DialogDescription>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("checkout.lockerSearch")}
                className="rpm-input w-full pl-10 pr-10"
                data-testid="locker-search-input"
              />
              {q && (
                <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg hover:bg-zinc-100" aria-label="clear">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </DialogHeader>
          <div className="min-h-[200px] flex-1 overflow-y-auto p-2" data-testid="locker-list">
            {state === "loading" && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-600">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.lockerLoading")}
              </div>
            )}
            {state === "error" && (
              <div className="py-10 text-center text-sm">
                <p className="text-zinc-700">{t("checkout.lockerError")}</p>
                <button type="button" onClick={load} className="rpm-btn rpm-btn-black mt-4" data-testid="locker-retry-button">
                  {t("checkout.lockerRetry")}
                </button>
              </div>
            )}
            {state === "ready" && filtered.length === 0 && <p className="py-12 text-center text-sm text-zinc-600">{t("checkout.lockerEmpty")}</p>}
            {state === "ready" &&
              filtered.slice(0, 150).map((i) => {
                const active = value?.id === i.id;
                return (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => {
                      onChange(i);
                      setOpen(false);
                    }}
                    className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${active ? "bg-[var(--rpm-lime)]" : "hover:bg-zinc-100"}`}
                    data-testid={`locker-option-${i.id}`}
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold leading-tight">{i.name}</div>
                      <div className="mt-0.5 truncate text-xs text-zinc-600">
                        {i.address} · <span className="font-medium text-zinc-800">{i.city}</span>
                      </div>
                    </div>
                    {active && <Check className="h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            {state === "ready" && filtered.length > 150 && (
              <p className="px-3 py-3 text-center text-xs text-zinc-500">+{filtered.length - 150} … {t("checkout.lockerSearch").toLowerCase()}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
